import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { AGGREGATION_TYPE, BillableMetric, MEASUREMENT_TYPE } from 'src/features/billableMetrics/utils/types';
import {
    billableMetricFormSchema,
    BillableMetricFormSchemaValues,
} from 'src/features/billableMetrics/utils/formSchema';
import BillableMetricForm from 'src/features/billableMetrics/components/forms/BillableMetricForm';
import { useGetBillableMetric } from 'src/features/billableMetrics/api/useGetBillableMetric';

interface BillableMetricFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedBillableMetric?: BillableMetric;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: BillableMetricFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function BillableMetricFormModal<TData>({
    selectedBillableMetric,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: BillableMetricFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { data: metricData } = useGetBillableMetric({
        metricId: selectedBillableMetric?.id ?? '',
        config: { enabled: !!selectedBillableMetric?.id && isModalOpen },
    });
    const onError = useOnError();

    /*
     * Measurement and aggregation default to a matched pair - an empty aggregation next to a
     * populated measurement is exactly the state the API rejects.
     */
    const defaultValues = React.useMemo(() => {
        return {
            name: metricData?.name ?? undefined,
            code: metricData?.code ?? undefined,
            measurement_type: metricData?.measurement_type ?? MEASUREMENT_TYPE.STATEFUL,
            aggregation_type: metricData?.aggregation_type ?? AGGREGATION_TYPE.TIME_WEIGHTED,
            description: metricData?.description ?? undefined,
            unit: metricData?.unit ?? undefined,
            is_active: metricData?.is_active ?? true,
        };
    }, [metricData]);

    const form = useForm<BillableMetricFormSchemaValues>({
        resolver: zodResolver(billableMetricFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: BillableMetricFormSchemaValues) => {
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <BillableMetricForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedBillableMetric && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default BillableMetricFormModal;
